import React from 'react';
import { useNavigate } from 'react-router-dom';
import ProductNav from '../components/ProductNav';
import { MoreInfoHeading as Heading } from '../components/moreInfo/CyberTechMoreInfoPrimitives';
import '../CyberTechDetailPage.css';
import './ImageComposerProductPage.css';

const ART = '/product-more-info/image-composer/product-art.webp';
const DIRECT_COMPOSITION = '/product-more-info/image-composer/direct-composition.jpg';
const DESTINATION_CANVASES = '/product-more-info/image-composer/destination-canvases.jpg';
const EXPORT_ROUTES = '/product-more-info/image-composer/export-routes.jpg';
const LOCAL_PROCESSING = '/product-more-info/image-composer/local-processing.jpg';

const workflow = [
  ['01', 'Bring in the source images', 'Add several images with the Add Image control or drop them directly onto the canvas.'],
  ['02', 'Build the composition', 'Move, resize and rotate each image directly, then use the layer controls to decide what sits in front.'],
  ['03', 'Crop and finish', 'Crop from any edge, flip or duplicate, then add borders, rounded corners, shadow and opacity where the composition needs them.'],
  ['04', 'Take one result away', 'Export PNG, transparent PNG, JPG or WebP, or copy the finished canvas straight into another piece of work.'],
];

const controls = [
  ['Position', 'Move each image directly on the canvas.'],
  ['Scale', 'Resize from the corners without leaving the composition.'],
  ['Rotate', 'Turn individual images until the visual balance is right.'],
  ['Layer', 'Bring forward, send backward, or move an image straight to the front or back.'],
  ['Crop', 'Trim from edges and corners while the image stays in place.'],
  ['Finish', 'Add shadow, border, rounded corners and opacity.'],
];

export default function ImageComposerProductPage() {
  const navigate = useNavigate();

  return (
    <main className="ctmi icmi">
      <ProductNav />

      <section className="icmi-hero" aria-labelledby="icmi-title">
        <div className="icmi-hero__grid" aria-hidden="true" />
        <img className="icmi-hero__art" src={ART} alt="" aria-hidden="true" />
        <div className="icmi-hero__shade" />
        <div className="ctmi-shell icmi-hero__content">
          <button className="ctmi-back" type="button" onClick={() => navigate('/dashboard/utilities/free')}>← Back to Free VISINVOKE Utilities</button>
          <span className="ctmi-kicker">Free utility · local image composition</span>
          <h1 id="icmi-title">IMAGE<br />COMPOSER</h1>
          <p className="icmi-hero__tagline">Several images in. One finished image out.</p>
          <p className="icmi-hero__intro">Combine separate images on one direct canvas. Arrange, layer, crop and finish them, then export a single composition that is ready for a presentation, document, email or the next VISINVOKE tool.</p>
          <div className="icmi-actions">
            <a className="ctmi-button" href="/image-composer/index.html">Open Image Composer</a>
            <a className="ctmi-button ctmi-button--ghost" href="#workspace">See the real workspace</a>
          </div>
          <div className="ctmi-facts">
            <div><strong>Free</strong><span>with your VISINVOKE account</span></div>
            <div><strong>Local</strong><span>runs on your computer</span></div>
            <div><strong>4</strong><span>export routes</span></div>
            <div><strong>1</strong><span>finished composition</span></div>
          </div>
        </div>
      </section>

      <nav className="ctmi-nav" aria-label="Image Composer More Info sections">
        <div className="ctmi-shell">
          <a href="#workspace">Workspace</a>
          <a href="#workflow">Workflow</a>
          <a href="#control">Direct control</a>
          <a href="#canvas">Canvas</a>
          <a href="#export">Export</a>
          <a href="#local">Local &amp; private</a>
        </div>
      </nav>

      <section className="ctmi-section icmi-intro" id="workspace">
        <div className="ctmi-shell">
          <Heading eyebrow="The actual utility" title="Work on the composition, not around it.">
            This is the real Image Composer workspace embedded below—not a generic diagram of what the product might look like. The canvas stays central while the controls remain close to the object you are arranging.
          </Heading>
          <div className="icmi-live">
            <div className="icmi-live__bar"><span>VISINVOKE IMAGE COMPOSER</span><strong>LIVE PRODUCT WORKSPACE</strong><a href="/image-composer/index.html">Open full size ↗</a></div>
            <iframe title="Live VISINVOKE Image Composer workspace" src="/image-composer/index.html?embedded-more-info=1" loading="eager" />
          </div>
        </div>
      </section>

      <section className="ctmi-section ctmi-tint" id="workflow">
        <div className="ctmi-shell">
          <Heading eyebrow="A direct four-part workflow" title="From loose images to one usable result.">
            There is no project setup ceremony. Add the material, shape the layout, finish the edges and take the completed canvas into the rest of your work.
          </Heading>
          <div className="icmi-workflow">
            {workflow.map(([number, title, copy]) => (
              <article key={number}><span>{number}</span><div><h3>{title}</h3><p>{copy}</p></div></article>
            ))}
          </div>
        </div>
      </section>

      <section className="icmi-principle">
        <div className="ctmi-shell icmi-principle__layout">
          <div>
            <span className="ctmi-kicker">The composition stays visible</span>
            <h2>Every adjustment happens in context.</h2>
            <p>Move an image and see the whole layout. Change its layer and see what it reveals. Crop it and judge the new balance immediately.</p>
          </div>
          <blockquote><small>THE CORE PRINCIPLE</small><p>The canvas is the work. The controls serve it.</p></blockquote>
        </div>
      </section>

      <section className="ctmi-section" id="control">
        <div className="ctmi-shell">
          <Heading eyebrow="Hands-on layout" title="Direct controls stay attached to a visual job.">
            Image Composer keeps ordinary composition work legible: one selected image, one visible canvas and the small set of actions that change the result.
          </Heading>
          <div className="icmi-control-stage">
            <div className="icmi-control-stage__art">
              <img src={DIRECT_COMPOSITION} alt="Separate source images arranged as one layered composition with direct crop, rotation and resize controls" />
            </div>
            <div className="icmi-control-stage__rail">
              {controls.map(([title, copy], index) => <article key={title}><span>{String(index + 1).padStart(2, '0')}</span><h3>{title}</h3><p>{copy}</p></article>)}
            </div>
          </div>
        </div>
      </section>

      <section className="icmi-cinema" id="canvas">
        <div className="icmi-cinema__wash" />
        <img src={DESTINATION_CANVASES} alt="One composition adapted for widescreen, square, portrait and custom destination canvases" />
        <div className="ctmi-shell icmi-cinema__copy">
          <span className="ctmi-kicker">A canvas that fits the destination</span>
          <h2>Build for widescreen, square, portrait—or an exact custom size.</h2>
          <p>Choose a transparent, white or coloured canvas, set the shape the final work actually needs, and keep the layout inside that boundary from the start.</p>
          <div className="icmi-readout"><span>16:9 <b>Presentation</b></span><span>1:1 <b>Square</b></span><span>Custom <b>Exact pixels</b></span></div>
        </div>
      </section>

      <section className="ctmi-section ctmi-tint" id="export">
        <div className="ctmi-shell icmi-export">
          <div>
            <Heading eyebrow="Finish once" title="The output leaves as one image.">
              The layers remain useful while you compose. The finished result becomes the single image your presentation, document, email or next tool needs.
            </Heading>
            <div className="icmi-formats"><span>PNG</span><span>Transparent PNG</span><span>JPG</span><span>WebP</span><span>Copy</span></div>
          </div>
          <figure>
            <img src={EXPORT_ROUTES} alt="One finished composition leaving through transparent, photographic, web and clipboard output routes" />
            <figcaption>Arrange many visual elements. Export one clean result.</figcaption>
          </figure>
        </div>
      </section>

      <section className="ctmi-section" id="local">
        <div className="ctmi-shell icmi-local">
          <figure className="icmi-local__visual">
            <img src={LOCAL_PROCESSING} alt="Source images, composition work and the saved result remaining inside one local computer boundary" />
            <figcaption>Source, composition and finished result remain on your machine.</figcaption>
          </figure>
          <div>
            <Heading eyebrow="Local and private" title="Your source images stay on your computer.">
              Image Composer is a genuine free VISINVOKE utility. It runs through the Desktop Command Centre or directly in your browser, and performs the composition work on your own machine.
            </Heading>
            <ul><li>No third-party image-editing account</li><li>No upload step to a separate composition service</li><li>No trial timer or reduced free edition</li><li>One VISINVOKE account across the utility estate</li></ul>
          </div>
        </div>
      </section>

      <footer className="icmi-footer">
        <div className="ctmi-shell">
          <span className="ctmi-kicker">VISINVOKE Image Composer</span>
          <h2>Turn the images you already have into the image you actually need.</h2>
          <p>Free with your VISINVOKE account. Run it through DCC or open it directly in your browser.</p>
          <div className="icmi-actions"><a className="ctmi-button" href="/image-composer/index.html">Open Image Composer</a><button className="ctmi-button ctmi-button--ghost" type="button" onClick={() => navigate('/dashboard/utilities/free')}>Back to Free Utilities</button></div>
        </div>
      </footer>
    </main>
  );
}
